import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Stack,
  Alert,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  TableContainer,
  Breadcrumbs,
  Link,
  Fade,
  Container,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { TenantDateField } from '../../components/TenantDateField';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../lib/error-text';
import {
  buildSectionCode,
  codesInTerm,
  freeSectionCode,
  sectionCodeAfterMove,
} from './configure-term.codes';
import {
  CheckCircleRounded,
  SearchRounded,
  ArrowBackRounded,
  SchoolRounded,
  SaveRounded,
  NavigateNextRounded,
  EditRounded,
  LooksOneRounded,
  LooksTwoRounded,
  AddRounded,
  CloseRounded,
  DragIndicatorRounded,
  DeleteOutlineRounded as _DeleteOutlineRounded,
  SwapHorizRounded,
  InfoOutlined,
} from '@mui/icons-material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  DragEndEvent,
  DragStartEvent,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import { collection, doc, onSnapshot, query, where, type DocumentData } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenantStore } from '../../stores/tenant.store';
import { useAcademic } from '../../hooks/use-academic';
import { db } from '../../lib/firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConfigRow {
  courseId: string;
  courseName: string;
  courseCode: string;
  sectionCode: string;
  programId: string;
  programName: string;
  existingSectionId?: string;
  facultyId: string;
  startDate: string;
  endDate: string;
  maxEnrollment: number | '';
}

/** A course assigned to a program within this term */
interface TermCourse {
  sectionId?: string; // undefined = pending create
  courseId: string;
  courseName: string;
  courseCode: string;
  sectionCode: string;
}

/** A program slot in the period */
interface TermProgram {
  programId: string;
  programName: string;
  programCode: string;
  courses: TermCourse[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toDateStr = (val: any): string => {
  if (!val) return '';
  let d: Date;
  if (val.toDate) {
    d = val.toDate();
  } else if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    // Pure date string: use as-is (already YYYY-MM-DD)
    return val;
  } else {
    d = new Date(val);
  }
  return d instanceof Date && !isNaN(d.getTime()) ? d.toISOString().substring(0, 10) : '';
};

const FACULTY_ROLES = [
  'faculty',
  'docente',
  'tutor',
  'decano',
  'academico',
  'coordinador',
  'capellan',
  'staff',
];

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepBar({ step }: { step: 1 | 2 }) {
  const steps = [
    { n: 1, label: 'Asignar materias', icon: <LooksOneRounded sx={{ fontSize: 18 }} /> },
    { n: 2, label: 'Configurar', icon: <LooksTwoRounded sx={{ fontSize: 18 }} /> },
  ];
  return (
    <Stack direction="row" spacing={0} sx={{ mb: 0 }}>
      {steps.map(({ n, label, icon }, i) => {
        const active = step === n;
        const done = step > n;
        return (
          <Stack key={n} direction="row" alignItems="center" sx={{ flex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                flex: 1,
                py: 1.5,
                px: 2,
                borderBottom: '2px solid',
                borderColor: active ? 'primary.main' : done ? 'success.main' : 'transparent',
                opacity: active ? 1 : done ? 0.7 : 0.4,
                transition: 'all 0.25s',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: active ? 'primary.main' : done ? 'success.main' : 'action.selected',
                  color: active || done ? '#fff' : 'text.secondary',
                  flexShrink: 0,
                  transition: 'background 0.25s',
                }}
              >
                {done ? <CheckCircleRounded sx={{ fontSize: 16 }} /> : icon}
              </Box>
              <Typography
                variant="body2"
                fontWeight={active ? 700 : 500}
                color={active ? 'primary.main' : 'text.secondary'}
                noWrap
              >
                {label}
              </Typography>
            </Stack>
            {i < steps.length - 1 && (
              <NavigateNextRounded sx={{ fontSize: 20, color: 'text.disabled', mx: 0.5 }} />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

// ─── Draggable course item ────────────────────────────────────────────────────

function DraggableCourseCard({
  id,
  courseName,
  courseCode,
  sectionCode,
  isAssigned,
}: {
  id: string;
  courseName: string;
  courseCode: string;
  sectionCode?: string;
  isAssigned?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        mb: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isAssigned ? 'success.dark' : 'divider',
        bgcolor: isAssigned ? 'rgba(34,197,94,0.08)' : 'background.paper',
        cursor: 'grab',
        // While dragging: completely hide the source element so only the DragOverlay is visible.
        // This prevents the "ghost at origin" flash when the drop completes.
        opacity: isDragging ? 0 : 1,
        pointerEvents: isDragging ? 'none' : 'auto',
        transition: 'opacity 0.1s',
        '&:hover': { borderColor: 'primary.light', bgcolor: 'rgba(99,102,241,0.06)' },
        userSelect: 'none',
      }}
    >
      <DragIndicatorRounded sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {courseName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {sectionCode || courseCode}
        </Typography>
      </Box>
      {isAssigned && (
        <CheckCircleRounded sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
      )}
    </Box>
  );
}

// ─── Droppable program panel ──────────────────────────────────────────────────

function DroppableProgramPanel({
  program,
  onRemoveCourse,
  onMoveTo,
  otherPrograms,
}: {
  program: TermProgram;
  onRemoveCourse: (programId: string, courseId: string) => void;
  onMoveTo: (fromProgramId: string, courseId: string, toProgramId: string) => void;
  otherPrograms: TermProgram[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `prog-${program.programId}` });
  const [movingCourseId, setMovingCourseId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        borderColor: isOver ? 'primary.main' : 'divider',
        borderWidth: isOver ? 2 : 1,
        transition: 'all 0.15s',
        bgcolor: isOver ? 'rgba(99,102,241,0.04)' : 'background.paper',
      }}
    >
      {/* Program header — click to collapse */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        onClick={() => setCollapsed((c) => !c)}
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: 'action.hover',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: 'action.selected' },
        }}
      >
        <SchoolRounded sx={{ fontSize: 16, color: 'primary.light' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {program.programName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Código: {program.programCode}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={program.courses.length}
          color={program.courses.length > 0 ? 'primary' : 'default'}
          variant="outlined"
          sx={{ fontSize: 11, height: 22 }}
        />
        {/* Collapse indicator */}
        <Box
          sx={{
            ml: 0.5,
            fontSize: 18,
            color: 'text.disabled',
            lineHeight: 1,
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        >
          ▾
        </Box>
      </Stack>

      <Divider />

      {/* Drop zone — hidden when collapsed */}
      {!collapsed && (
        <Box ref={setNodeRef} sx={{ minHeight: 80, p: 1.5 }}>
          {program.courses.length === 0 ? (
            <Box
              sx={{
                minHeight: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1.5,
                border: '2px dashed',
                borderColor: isOver ? 'primary.main' : 'divider',
                color: 'text.disabled',
                fontSize: 13,
                transition: 'all 0.15s',
              }}
            >
              {isOver ? 'Soltar aquí' : 'Arrastra materias aquí'}
            </Box>
          ) : (
            <Stack spacing={0.5}>
              {program.courses.map((c) => (
                <Stack key={c.courseId} direction="row" alignItems="center" gap={0.5}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                      }}
                    >
                      <DragIndicatorRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap fontSize={13}>
                          {c.courseName}
                        </Typography>
                        <Typography variant="caption" color="primary.light" fontWeight={600}>
                          {c.sectionCode}
                        </Typography>
                      </Box>
                      {c.sectionId && (
                        <Tooltip title="Ya configurada en este período">
                          <CheckCircleRounded sx={{ fontSize: 13, color: 'success.main' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  {/* Move to another program */}
                  {otherPrograms.length > 0 && (
                    <Tooltip title="Mover a otro programa">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMovingCourseId(c.courseId);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <SwapHorizRounded sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Remove */}
                  <Tooltip title={c.sectionId ? 'Cancelar sección' : 'Quitar'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveCourse(program.programId, c.courseId);
                      }}
                      sx={{ p: 0.5, color: 'error.light' }}
                    >
                      <CloseRounded sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Collapsed hint — always droppable even when collapsed */}
      {collapsed && (
        <Box
          ref={setNodeRef}
          sx={{
            px: 2,
            py: 0.75,
            fontSize: 12,
            color: isOver ? 'primary.main' : 'text.disabled',
            textAlign: 'center',
            bgcolor: isOver ? 'rgba(99,102,241,0.06)' : 'transparent',
            transition: 'all 0.15s',
            borderTop: '1px dashed',
            borderColor: isOver ? 'primary.main' : 'divider',
          }}
        >
          {isOver
            ? 'Soltar aquí'
            : `${program.courses.length} materia${program.courses.length !== 1 ? 's' : ''} — haz click para expandir`}
        </Box>
      )}

      {/* Move dialog */}
      <Dialog
        open={!!movingCourseId}
        onClose={() => setMovingCourseId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Mover a otro programa</DialogTitle>
        <DialogContent>
          <List dense disablePadding>
            {otherPrograms.map((p) => (
              <ListItemButton
                key={p.programId}
                onClick={() => {
                  if (movingCourseId) {
                    onMoveTo(program.programId, movingCourseId, p.programId);
                    setMovingCourseId(null);
                  }
                }}
                sx={{ borderRadius: 1 }}
              >
                <SchoolRounded sx={{ fontSize: 15, mr: 1.5, color: 'primary.light' }} />
                <ListItemText
                  primary={p.programName}
                  secondary={`Código: ${p.programCode}`}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovingCourseId(null)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ─── Add Program Dialog ───────────────────────────────────────────────────────

function AddProgramDialog({
  open,
  onClose,
  activePrograms,
  usedProgramIds,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  activePrograms: any[];
  usedProgramIds: Set<string>;
  onAdd: (programs: any[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const available = activePrograms.filter((p) => !usedProgramIds.has(p.id));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const handleAdd = () => {
    const toAdd = activePrograms.filter((p) => selected.has(p.id));
    onAdd(toAdd);
    setSelected(new Set());
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar programas al período</DialogTitle>
      <DialogContent>
        {available.length === 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
            Todos los programas activos ya están en este período.
          </Typography>
        ) : (
          <List dense disablePadding>
            {available.map((p) => (
              <ListItemButton key={p.id} onClick={() => toggle(p.id)} sx={{ borderRadius: 1 }}>
                <Checkbox checked={selected.has(p.id)} size="small" sx={{ p: 0.5, mr: 1 }} />
                <SchoolRounded sx={{ fontSize: 15, mr: 1.5, color: 'primary.light' }} />
                <ListItemText
                  primary={p.name}
                  secondary={`Código: ${p.code}`}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={selected.size === 0} onClick={handleAdd}>
          Agregar {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Step 1: Split Panel ──────────────────────────────────────────────────────

interface Step1Props {
  termId: string;
  termPrograms: TermProgram[];
  catalogCourses: any[];
  activePrograms: any[];
  existingSections: any[];
  onAssign: (courseId: string, programId: string) => Promise<void>;
  onRemove: (programId: string, courseId: string) => Promise<void>;
  onMove: (fromProgramId: string, courseId: string, toProgramId: string) => Promise<void>;
  onAddPrograms: (programs: any[]) => void;
  opInProgress: boolean;
}

function Step1SplitPanel({
  termPrograms,
  catalogCourses,
  activePrograms,
  onAssign,
  onRemove,
  onMove,
  onAddPrograms,
  opInProgress,
}: Step1Props) {
  const [search, setSearch] = useState('');
  const [addProgramOpen, setAddProgramOpen] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Catalog courses filtered by search (a course can appear in MULTIPLE programs)
  const filteredCatalog = useMemo(() => {
    const q = search.toLowerCase().trim();
    return catalogCourses
      .filter((c) => !c.deletedAt && c.status === 'active')
      .filter((c) => !q || c.name?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [catalogCourses, search]);

  // Track which (courseId, programId) pairs are already assigned
  const assignedPairs = useMemo(
    () =>
      new Set(termPrograms.flatMap((p) => p.courses.map((c) => `${c.courseId}::${p.programId}`))),
    [termPrograms],
  );

  const usedProgramIds = useMemo(
    () => new Set(termPrograms.map((p) => p.programId)),
    [termPrograms],
  );

  const activeDragCourse = activeDragId ? catalogCourses.find((c) => c.id === activeDragId) : null;

  const handleDragStart = (e: DragStartEvent) => setActiveDragId(String(e.active.id));

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveDragId(null);
    const { over, active } = e;
    if (!over) return;
    const dropId = String(over.id);
    if (!dropId.startsWith('prog-')) return;
    const targetProgramId = dropId.replace('prog-', '');
    const courseId = String(active.id);

    // Only block if the exact same course is already in the exact same program
    if (assignedPairs.has(`${courseId}::${targetProgramId}`)) return;

    // Assign from catalog (same course can exist in multiple programs — that's by design)
    await onAssign(courseId, targetProgramId);
  };

  return (
    <>
      {/* Fix #4: pointerWithin first, rectIntersection as fallback for precise drop targeting */}
      <DndContext
        sensors={sensors}
        collisionDetection={(args) => {
          const pointerHits = pointerWithin(args);
          if (pointerHits.length > 0) return pointerHits;
          return rectIntersection(args);
        }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ minHeight: 500 }}>
          {/* ── Left: Catalog (40%) ── */}
          <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '40%' } }}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', flexShrink: 0 }}
              >
                <Typography variant="body2" fontWeight={700}>
                  Catálogo de cursos
                </Typography>
                <Chip
                  size="small"
                  label={`${filteredCatalog.length} disponibles`}
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
                <Tooltip title="Arrastra un curso al programa donde quieres asignarlo, o usa el botón + en cada fila.">
                  <InfoOutlined sx={{ fontSize: 15, color: 'text.disabled', ml: 'auto' }} />
                </Tooltip>
              </Stack>

              <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
                <TextField
                  size="small"
                  placeholder="Buscar por nombre o código..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded sx={{ fontSize: 17, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1.5 }}>
                {filteredCatalog.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    textAlign="center"
                    sx={{ pt: 4 }}
                  >
                    No se encontraron cursos
                  </Typography>
                ) : (
                  filteredCatalog.map((c) => {
                    // A course is "assigned" if it's in ALL programs (for visual cue only)
                    const assignedInCount = termPrograms.filter((p) =>
                      p.courses.some((x) => x.courseId === c.id),
                    ).length;
                    const isFullyAssigned =
                      termPrograms.length > 0 && assignedInCount === termPrograms.length;
                    return (
                      <Stack key={c.id} direction="row" alignItems="center" gap={0.5}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <DraggableCourseCard
                            id={c.id}
                            courseName={c.name}
                            courseCode={c.code}
                            isAssigned={isFullyAssigned}
                          />
                        </Box>
                        {/* Assign button — allows same course in multiple programs */}
                        {termPrograms.length > 0 && (
                          <AssignButton
                            courseId={c.id}
                            courseName={c.name}
                            programs={termPrograms}
                            assignedPairs={assignedPairs}
                            onAssign={onAssign}
                            disabled={opInProgress}
                          />
                        )}
                      </Stack>
                    );
                  })
                )}
              </Box>
            </Paper>
          </Box>

          {/* ── Right: Programs (60%) ── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={1.5}>
              {/* Header */}
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                  Programas en este período
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddRounded />}
                  onClick={() => setAddProgramOpen(true)}
                  disabled={opInProgress}
                >
                  Agregar programa
                </Button>
              </Stack>

              {termPrograms.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 5, textAlign: 'center', borderRadius: 2.5, borderStyle: 'dashed' }}
                >
                  <SchoolRounded sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.disabled">
                    No hay programas en este período aún.
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Haz click en "Agregar programa" para comenzar.
                  </Typography>
                </Paper>
              ) : (
                termPrograms.map((prog) => (
                  <DroppableProgramPanel
                    key={prog.programId}
                    program={prog}
                    onRemoveCourse={async (pid, cid) => {
                      await onRemove(pid, cid);
                    }}
                    onMoveTo={async (fromPid, cid, toId) => {
                      await onMove(fromPid, cid, toId);
                    }}
                    otherPrograms={termPrograms.filter((p) => p.programId !== prog.programId)}
                  />
                ))
              )}
            </Stack>
          </Box>
        </Stack>

        {/* Drag overlay */}
        {/* dropAnimation=null removes the "fly back" animation when a card is dropped */}
        <DragOverlay dropAnimation={null}>
          {activeDragCourse ? (
            <Paper
              elevation={8}
              sx={{ px: 2, py: 1, borderRadius: 1.5, cursor: 'grabbing', minWidth: 180 }}
            >
              <Typography variant="body2" fontWeight={700} noWrap>
                {activeDragCourse.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activeDragCourse.code}
              </Typography>
            </Paper>
          ) : null}
        </DragOverlay>
      </DndContext>

      <AddProgramDialog
        open={addProgramOpen}
        onClose={() => setAddProgramOpen(false)}
        activePrograms={activePrograms}
        usedProgramIds={usedProgramIds}
        onAdd={onAddPrograms}
      />
    </>
  );
}

// ─── Assign Button (for when there are programs but no drag preference) ────────

function AssignButton({
  courseId,
  courseName,
  programs,
  assignedPairs,
  onAssign,
  disabled,
}: {
  courseId: string;
  courseName: string;
  programs: TermProgram[];
  assignedPairs: Set<string>;
  onAssign: (courseId: string, programId: string) => Promise<void>;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);

  // Filter out programs where this course is already assigned
  const available = programs.filter((p) => !assignedPairs.has(`${courseId}::${p.programId}`));

  if (available.length === 0) return null;

  if (available.length === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const target = available[0]!;
    return (
      <Tooltip title={`Asignar a ${target.programName}`}>
        <IconButton
          size="small"
          onClick={() => onAssign(courseId, target.programId)}
          disabled={disabled}
          sx={{ p: 0.5, color: 'primary.main' }}
        >
          <AddRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="Asignar a programa…">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          disabled={disabled}
          sx={{ p: 0.5, color: 'primary.main' }}
        >
          <AddRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Asignar "{courseName}" a…</DialogTitle>
        <DialogContent>
          <List dense disablePadding>
            {available.map((p) => (
              <ListItemButton
                key={p.programId}
                onClick={() => {
                  onAssign(courseId, p.programId);
                  setOpen(false);
                }}
                sx={{ borderRadius: 1 }}
              >
                <SchoolRounded sx={{ fontSize: 15, mr: 1.5, color: 'primary.light' }} />
                <ListItemText
                  primary={p.programName}
                  secondary={`Código: ${p.programCode}`}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Step 2: Config Table ─────────────────────────────────────────────────────

interface ConfigTableProps {
  rows: ConfigRow[];
  faculty: any[];
  onUpdate: <K extends keyof ConfigRow>(courseId: string, field: K, value: ConfigRow[K]) => void;
}

function ConfigTable({ rows, faculty, onUpdate }: ConfigTableProps) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              '& th': { fontWeight: 700, bgcolor: 'background.default', whiteSpace: 'nowrap' },
            }}
          >
            <TableCell>Materia</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Programa</TableCell>
            <TableCell sx={{ minWidth: 200 }}>Docente</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Fecha Inicio</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Fecha Fin</TableCell>
            <TableCell sx={{ minWidth: 90 }}>Cupos</TableCell>
            <TableCell sx={{ minWidth: 60 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 8, color: 'text.disabled' }}>
                <SchoolRounded sx={{ fontSize: 32, display: 'block', mx: 'auto', mb: 1 }} />
                Asigna materias a los programas en el Paso 1
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.courseId} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {row.courseName}
                  </Typography>
                  <Chip
                    label={row.sectionCode || row.courseCode}
                    size="small"
                    sx={{ fontSize: 10, height: 18, mt: 0.3 }}
                    color={row.existingSectionId ? 'warning' : 'primary'}
                    variant="outlined"
                  />
                </TableCell>

                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="caption" color="text.secondary">
                    {row.programName}
                  </Typography>
                </TableCell>

                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={row.facultyId}
                    onChange={(e) => onUpdate(row.courseId, 'facultyId', e.target.value)}
                  >
                    <MenuItem value="">Sin asignar</MenuItem>
                    {faculty.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.firstName} {f.lastName}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>

                <TableCell>
                  <TenantDateField
                    size="small"
                    fullWidth
                    value={row.startDate}
                    onChange={(v) => onUpdate(row.courseId, 'startDate', v)}
                  />
                </TableCell>

                <TableCell>
                  <TenantDateField
                    size="small"
                    fullWidth
                    value={row.endDate}
                    onChange={(v) => onUpdate(row.courseId, 'endDate', v)}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    fullWidth
                    value={row.maxEnrollment}
                    onChange={(e) =>
                      onUpdate(
                        row.courseId,
                        'maxEnrollment',
                        e.target.value ? Number(e.target.value) : '',
                      )
                    }
                    placeholder="—"
                  />
                </TableCell>

                <TableCell>
                  <Chip
                    icon={
                      row.existingSectionId ? (
                        <EditRounded sx={{ fontSize: 12 }} />
                      ) : (
                        <CheckCircleRounded sx={{ fontSize: 12 }} />
                      )
                    }
                    label={row.existingSectionId ? 'Editar' : 'Nueva'}
                    size="small"
                    color={row.existingSectionId ? 'warning' : 'success'}
                    variant="outlined"
                    sx={{ fontSize: 11 }}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ConfigureTermPage() {
  const { t } = useTranslation('sis');
  const { termId } = useParams<{ termId: string }>();
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const { createSection, updateSection } = useAcademic();

  // Remote data
  const [term, setTerm] = useState<any>(null);
  const [courses, setCourses] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [faculty, setFaculty] = useState<any[]>([]);
  const [existingSections, setExistingSections] = useState<any[]>([]);

  // Track initial load completion per critical collection (sections, programs, courses)
  const [sectionsReady, setSectionsReady] = useState(false);
  const [programsReady, setProgramsReady] = useState(false);
  const [coursesReady, setCoursesReady] = useState(false);
  const [facultyReady, setFacultyReady] = useState(false);

  const loadingData = !sectionsReady || !programsReady || !coursesReady || !facultyReady;

  // Step 1 state — term-level program configuration (immediate)
  const [termPrograms, setTermPrograms] = useState<TermProgram[]>([]);
  const [opInProgress, setOpInProgress] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);

  // Wizard step
  const [step, setStep] = useState<1 | 2>(1);

  // Step 2 state
  const [configRows, setConfigRows] = useState<ConfigRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);

  const activePrograms = useMemo(
    () => programs.filter((p) => !p.deletedAt && p.status !== 'inactive'),
    [programs],
  );

  const facultyPeople = useMemo(
    () =>
      faculty.filter(
        (p) =>
          p.types?.some((t: string) => FACULTY_ROLES.includes(t)) ||
          p.institutionalRoles?.some((r: string) => FACULTY_ROLES.includes(r)),
      ),
    [faculty],
  );

  useEffect(() => {
    if (!currentTenantId || !termId) return;

    // Reset readiness when tenant/term changes
    setSectionsReady(false);
    setProgramsReady(false);
    setCoursesReady(false);
    setFacultyReady(false);

    const unsubs: (() => void)[] = [];

    // SOLO lo de este período: antes escuchaba todas las secciones y todas las
    // personas del seminario (con historia importada, ~2.000 lecturas) para
    // configurar un período de ~10 secciones.
    unsubs.push(
      onSnapshot(doc(db, `tenants/${currentTenantId}/terms/${termId}`), (d) => {
        setTerm(d.exists() ? { id: d.id, ...d.data() } : null);
      }),
    );
    unsubs.push(
      onSnapshot(collection(db, `tenants/${currentTenantId}/courses`), (snap) => {
        setCourses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setCoursesReady(true);
      }),
    );
    unsubs.push(
      onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
        setPrograms(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setProgramsReady(true);
      }),
    );
    unsubs.push(
      onSnapshot(
        query(collection(db, `tenants/${currentTenantId}/sections`), where('termId', '==', termId)),
        (snap) => {
          setExistingSections(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setSectionsReady(true);
        },
      ),
    );
    // Docentes: quien tenga un rol docente por tipo O por cargo institucional.
    // Dos consultas de un solo campo (sin índice compuesto) en vez de todas las
    // personas; el filtro de `facultyPeople` sigue siendo el mismo.
    const facultyParts: Record<'types' | 'institutionalRoles', DocumentData[] | null> = {
      types: null,
      institutionalRoles: null,
    };
    const publishFaculty = () => {
      if (!facultyParts.types || !facultyParts.institutionalRoles) return;
      const byId = new Map<string, DocumentData>();
      for (const p of [...facultyParts.types, ...facultyParts.institutionalRoles]) {
        // Igual que antes (`deletedAt == null`): solo los que tienen el campo en null.
        if (p.deletedAt === null) byId.set(p.id, p);
      }
      setFaculty([...byId.values()]);
      setFacultyReady(true);
    };
    for (const field of ['types', 'institutionalRoles'] as const) {
      unsubs.push(
        onSnapshot(
          query(
            collection(db, `tenants/${currentTenantId}/people`),
            where(field, 'array-contains-any', FACULTY_ROLES),
          ),
          (snap) => {
            facultyParts[field] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            publishFaculty();
          },
          // Sin el plantel no hay a quién asignar: se dice, y la pantalla no
          // queda esperándolo.
          (e) => {
            setOpError(errorText(e));
            publishFaculty();
          },
        ),
      );
    }

    return () => unsubs.forEach((u) => u());
  }, [currentTenantId, termId]);

  // Build termPrograms from existing sections when data loads
  useEffect(() => {
    if (loadingData || !termId || programs.length === 0) return;

    const activeSections = existingSections.filter(
      (s) => s.termId === termId && s.status !== 'cancelled',
    );

    // Build programs from Firestore sections
    const programMap = new Map<string, TermProgram>();

    for (const s of activeSections) {
      const prog = programs.find((p) => p.id === s.programId);
      if (!prog) continue;
      const course = courses.find((c) => c.id === s.courseId);
      if (!course) continue;

      if (!programMap.has(s.programId)) {
        programMap.set(s.programId, {
          programId: prog.id,
          programName: prog.name,
          programCode: prog.code || '',
          courses: [],
        });
      }
      programMap.get(s.programId)!.courses.push({
        sectionId: s.id,
        courseId: s.courseId,
        courseName: course.name,
        courseCode: course.code,
        sectionCode: s.sectionCode || buildSectionCode(prog.code, course.code),
      });
    }

    // Merge: update or add programs from Firestore, but KEEP locally-added programs
    // (those with no Firestore sections — e.g. user just added via "Agregar programa").
    // Use functional update to access previous state without adding it as a dependency.
    setTermPrograms((prev) => {
      const firestorePrograms = Array.from(programMap.values());

      // ── Fix #1: Stable order ─────────────────────────────────────────────
      // Sort firestorePrograms to match the existing order in `prev`.
      // New programs (not in prev) are appended in alphabetical order.
      const prevOrder = new Map(prev.map((p, i) => [p.programId, i]));
      firestorePrograms.sort((a, b) => {
        const ia = prevOrder.get(a.programId) ?? Infinity;
        const ib = prevOrder.get(b.programId) ?? Infinity;
        if (ia !== Infinity || ib !== Infinity) return ia - ib;
        return a.programName.localeCompare(b.programName);
      });

      const firestoreIds = new Set(firestorePrograms.map((p) => p.programId));

      // Local-only programs (no sections in Firestore yet) — preserve their position
      const localOnly = prev.filter((p) => !firestoreIds.has(p.programId));

      return [...firestorePrograms, ...localOnly];
    });
  }, [existingSections, courses, programs, termId, loadingData]);

  // ── Step 1 actions (immediate — no accumulation) ─────────────────────────

  const handleAssign = useCallback(
    async (courseId: string, programId: string) => {
      if (!currentTenantId || !termId) return;
      setOpError(null);

      const prog = activePrograms.find((p) => p.id === programId);
      const course = courses.find((c) => c.id === courseId);
      if (!prog || !course) return;

      // Libre en el período (H7: el servidor lo exige único). Si el código
      // generado ya lo usa otra sección, se le agrega «-2», «-3»…
      const sectionCode = freeSectionCode(
        prog.code,
        course.code,
        codesInTerm(existingSections, termId),
      );

      // ── Optimistic update ──────────────────────────────────────────────────
      // Add course to program immediately so the UI feels instant (no flicker).
      // We keep a snapshot to roll back if the backend call fails.
      let snapshot: TermProgram[] = [];
      setTermPrograms((prev) => {
        snapshot = prev;
        const already = prev
          .find((p) => p.programId === programId)
          ?.courses.some((c) => c.courseId === courseId);
        if (already) return prev;
        return prev.map((p) =>
          p.programId === programId
            ? {
                ...p,
                courses: [
                  ...p.courses,
                  { courseId, courseName: course.name, courseCode: course.code, sectionCode },
                ],
              }
            : p,
        );
      });

      setOpInProgress(true);
      try {
        // ── Fix #5: Filter cancelled sections by programId too ────────────────
        const cancelled = existingSections.find(
          (s) =>
            s.courseId === courseId &&
            s.programId === programId &&
            s.termId === termId &&
            s.status === 'cancelled',
        );

        if (cancelled) {
          await updateSection({
            tenantId: currentTenantId,
            sectionId: cancelled.id,
            status: 'open',
            programId,
            // Reabrir conserva el código de la sección: no se le manda uno.
          });
        } else {
          await createSection({
            tenantId: currentTenantId,
            programId,
            courseId,
            termId,
            sectionCode,
          });
        }
        // Firestore subscription will reconcile termPrograms via useEffect
      } catch (e) {
        // Roll back optimistic update on error
        setTermPrograms(snapshot);
        setOpError(errorText(e, t('sections.configureErrors.assign')));
      } finally {
        setOpInProgress(false);
      }
    },
    [
      currentTenantId,
      termId,
      activePrograms,
      courses,
      existingSections,
      createSection,
      updateSection,
      t,
    ],
  );

  const handleRemove = useCallback(
    async (programId: string, courseId: string) => {
      if (!currentTenantId || !termId) return;
      setOpError(null);

      const section = existingSections.find(
        (s) =>
          s.courseId === courseId &&
          s.programId === programId &&
          s.termId === termId &&
          s.status !== 'cancelled',
      );

      // ── Fix #2: Optimistic update for remove ─────────────────────────────
      let snapshot: TermProgram[] = [];
      setTermPrograms((prev) => {
        snapshot = prev;
        return prev.map((p) =>
          p.programId === programId
            ? { ...p, courses: p.courses.filter((c) => c.courseId !== courseId) }
            : p,
        );
      });

      if (!section) {
        // Was a local-only (pending) course — already removed from state above
        return;
      }

      setOpInProgress(true);
      try {
        await updateSection({
          tenantId: currentTenantId,
          sectionId: section.id,
          status: 'cancelled',
        });
      } catch (e: any) {
        // Roll back on error
        setTermPrograms(snapshot);
        setOpError(e.message || 'Error al quitar la materia');
      } finally {
        setOpInProgress(false);
      }
    },
    [currentTenantId, termId, existingSections, updateSection],
  );

  const handleMove = useCallback(
    async (fromProgramId: string, courseId: string, toProgramId: string) => {
      if (!currentTenantId || !termId) return;
      setOpError(null);

      const section = existingSections.find(
        (s) =>
          s.courseId === courseId &&
          s.programId === fromProgramId &&
          s.termId === termId &&
          s.status !== 'cancelled',
      );
      const course = courses.find((c) => c.id === courseId);
      const targetProg = activePrograms.find((p) => p.id === toProgramId);
      if (!course || !targetProg) return;

      // El código solo cambia si esta pantalla lo había generado para el
      // programa de origen; un código propio o importado se conserva, y a una
      // sección sin código no se le manda uno (H7).
      const fromProg = activePrograms.find((p) => p.id === fromProgramId);
      const nextCode = section
        ? sectionCodeAfterMove({
            currentCode: section.sectionCode as string | undefined,
            fromProgramCode: fromProg?.code ?? '',
            toProgramCode: targetProg.code,
            courseCode: course.code,
            takenInTerm: codesInTerm(existingSections, termId, section.id),
          })
        : undefined;
      const sectionCode =
        nextCode ??
        ((section?.sectionCode as string | undefined) ||
          buildSectionCode(targetProg.code, course.code));

      // ── Fix #3: Optimistic update for move ──────────────────────────────
      let snapshot: TermProgram[] = [];
      setTermPrograms((prev) => {
        snapshot = prev;
        // Remove from source program
        const withoutSource = prev.map((p) =>
          p.programId === fromProgramId
            ? { ...p, courses: p.courses.filter((c) => c.courseId !== courseId) }
            : p,
        );
        // Add to target program (preserve existing sectionId if available)
        const existingCourse = prev
          .find((p) => p.programId === fromProgramId)
          ?.courses.find((c) => c.courseId === courseId);
        return withoutSource.map((p) =>
          p.programId === toProgramId
            ? {
                ...p,
                courses: [
                  ...p.courses,
                  {
                    ...(existingCourse ?? {
                      courseId,
                      courseName: course.name,
                      courseCode: course.code,
                    }),
                    sectionCode,
                    programId: toProgramId,
                  },
                ],
              }
            : p,
        );
      });

      if (!section) return; // local-only course, optimistic already applied

      setOpInProgress(true);
      try {
        await updateSection({
          tenantId: currentTenantId,
          sectionId: section.id,
          programId: toProgramId,
          ...(nextCode !== undefined && { sectionCode: nextCode }),
        });
      } catch (e) {
        // Roll back
        setTermPrograms(snapshot);
        setOpError(errorText(e, t('sections.configureErrors.move')));
      } finally {
        setOpInProgress(false);
      }
    },
    [currentTenantId, termId, existingSections, courses, activePrograms, updateSection, t],
  );

  const handleAddPrograms = useCallback((progsToAdd: any[]) => {
    setTermPrograms((prev) => {
      const existing = new Set(prev.map((p) => p.programId));
      const newProgs: TermProgram[] = progsToAdd
        .filter((p) => !existing.has(p.id))
        .map((p) => ({
          programId: p.id,
          programName: p.name,
          programCode: p.code || '',
          courses: [],
        }));
      return [...prev, ...newProgs];
    });
  }, []);

  // ── Step 2 transition ─────────────────────────────────────────────────────

  const handleContinue = () => {
    const rows: ConfigRow[] = termPrograms.flatMap((prog) =>
      prog.courses.map((c) => {
        const existing = existingSections.find(
          (s) =>
            s.courseId === c.courseId &&
            s.programId === prog.programId &&
            s.termId === termId &&
            s.status !== 'cancelled',
        );
        return {
          courseId: c.courseId,
          courseName: c.courseName,
          courseCode: c.courseCode,
          sectionCode: c.sectionCode,
          programId: prog.programId,
          programName: prog.programName,
          existingSectionId: existing?.id,
          facultyId: existing?.facultyId ?? '',
          startDate: toDateStr(existing?.startDate),
          endDate: toDateStr(existing?.endDate),
          maxEnrollment: existing?.maxEnrollment ?? '',
        } satisfies ConfigRow;
      }),
    );

    rows.sort(
      (a, b) =>
        a.programName.localeCompare(b.programName) || a.courseName.localeCompare(b.courseName),
    );

    setConfigRows(rows);
    setStep(2);
    setSaveError(null);
    setSavedCount(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateRow = <K extends keyof ConfigRow>(courseId: string, field: K, value: ConfigRow[K]) =>
    setConfigRows((prev) =>
      prev.map((r) => (r.courseId === courseId ? { ...r, [field]: value } : r)),
    );

  const handleSave = async () => {
    if (!currentTenantId) return;
    setSaving(true);
    setSaveError(null);
    setSavedCount(null);
    let count = 0;
    try {
      for (const row of configRows) {
        const facultyId = row.facultyId || undefined;
        const maxEnrollment = row.maxEnrollment !== '' ? Number(row.maxEnrollment) : undefined;
        if (row.existingSectionId) {
          await updateSection({
            tenantId: currentTenantId,
            sectionId: row.existingSectionId,
            ...(facultyId !== undefined && { facultyId }),
            ...(maxEnrollment !== undefined && { maxEnrollment }),
            startDate: row.startDate || null,
            endDate: row.endDate || null,
          });
        } else {
          await createSection({
            tenantId: currentTenantId,
            programId: row.programId,
            courseId: row.courseId,
            termId: termId!,
            ...(facultyId !== undefined && { facultyId }),
            ...(maxEnrollment !== undefined && { maxEnrollment }),
          });
        }
        count++;
      }
      setSavedCount(count);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setSaveError(err.message || 'Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary">
            Cargando período…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!term) {
    return (
      <Container maxWidth="sm" sx={{ pt: 6 }}>
        <Alert severity="error">
          Período no encontrado.{' '}
          <Link onClick={() => navigate('/sections')} sx={{ cursor: 'pointer' }}>
            Volver a Períodos Académicos
          </Link>
        </Alert>
      </Container>
    );
  }

  const totalCourses = termPrograms.reduce((s, p) => s + p.courses.length, 0);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100%', pb: 8 }}>
      {/* HEADER */}
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          pt: { xs: 2, sm: 3 },
          pb: 0,
          background: 'linear-gradient(160deg, rgba(99,102,241,0.08) 0%, transparent 60%)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Breadcrumbs separator={<NavigateNextRounded sx={{ fontSize: 15 }} />} sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="text.secondary"
            sx={{ cursor: 'pointer', fontSize: 13 }}
            onClick={() => navigate('/sections')}
          >
            Períodos Académicos
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            sx={{ cursor: 'pointer', fontSize: 13 }}
            onClick={() => navigate(`/sections/term/${termId}`)}
          >
            {term.name}
          </Link>
          <Typography color="text.primary" fontSize={13} fontWeight={600}>
            Configurar materias
          </Typography>
        </Breadcrumbs>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          justifyContent="space-between"
          gap={1}
          mb={2.5}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Typography variant="h4" fontWeight={800} color="white" letterSpacing={-0.5}>
                {term.name}
              </Typography>
              {term.status && (
                <Chip
                  label={term.status}
                  size="small"
                  color={term.status === 'open' ? 'success' : 'default'}
                  sx={{ fontWeight: 700, fontSize: 11, height: 22 }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Asigna materias a los programas y configura docentes y fechas
            </Typography>
          </Box>
        </Stack>

        <StepBar step={step} />
      </Box>

      {/* BODY */}
      <Box sx={{ px: { xs: 2, sm: 4 }, pt: 3 }}>
        {/* Alerts */}
        <Box sx={{ mb: opError || saveError || savedCount !== null ? 2 : 0 }}>
          {opError && (
            <Alert severity="error" onClose={() => setOpError(null)} sx={{ mb: 1 }}>
              {opError}
            </Alert>
          )}
          {saveError && (
            <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 1 }}>
              {saveError}
            </Alert>
          )}
          {savedCount !== null && (
            <Alert severity="success" onClose={() => setSavedCount(null)}>
              ✅ {savedCount} sección{savedCount !== 1 ? 'es' : ''} guardada
              {savedCount !== 1 ? 's' : ''} correctamente.
            </Alert>
          )}
        </Box>

        {/* ── STEP 1 ────────────────────────────── */}
        {step === 1 && (
          <Fade in timeout={300}>
            <Box>
              {opInProgress && (
                <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={16} />}>
                  Guardando cambios…
                </Alert>
              )}
              <Step1SplitPanel
                termId={termId!}
                termPrograms={termPrograms}
                catalogCourses={courses}
                activePrograms={activePrograms}
                existingSections={existingSections}
                onAssign={handleAssign}
                onRemove={handleRemove}
                onMove={handleMove}
                onAddPrograms={handleAddPrograms}
                opInProgress={opInProgress}
              />
            </Box>
          </Fade>
        )}

        {/* ── STEP 2 ────────────────────────────── */}
        {step === 2 && (
          <Fade in timeout={300}>
            <Box>
              <ConfigTable rows={configRows} faculty={facultyPeople} onUpdate={updateRow} />
            </Box>
          </Fade>
        )}
      </Box>

      {/* BOTTOM ACTION BAR */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 4 },
          py: 1.5,
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {step === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {termPrograms.length} programa{termPrograms.length !== 1 ? 's' : ''} · {totalCourses}{' '}
              materia{totalCourses !== 1 ? 's' : ''} asignada{totalCourses !== 1 ? 's' : ''}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(`/sections/term/${termId}`)}
              startIcon={<ArrowBackRounded />}
            >
              Volver al detalle
            </Button>
            <Button
              variant="contained"
              disabled={totalCourses === 0 || opInProgress}
              onClick={handleContinue}
              endIcon={<NavigateNextRounded />}
            >
              Continuar → Configurar
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {configRows.length} sección{configRows.length !== 1 ? 'es' : ''} para configurar
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setStep(1)}
              startIcon={<ArrowBackRounded />}
              disabled={saving}
            >
              ← Volver
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || configRows.length === 0}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRounded />}
              sx={{ minWidth: 160 }}
            >
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
